import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import CollaborationStrip from '@/components/CollaborationStrip';
import JollyDictationButton from '@/components/JollyDictationButton';
import MatterWorkspaceContext, { withQueryParams } from '@/components/matter/MatterWorkspaceContext';
import {
  ArrowLeft,
  Save,
  Share2,
  Download,
  Check,
  Loader2,
  Send,
  X,
  FileDown,
  FileText,
  MessageSquare,
  ChevronRight,
  Columns,
  ExternalLink,
  ArrowUp,
  ArrowDown,
  Plus,
  Sparkles,
  Bot,
  ShieldCheck,
  LayoutList,
  BookOpen,
  Copy,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Scale,
  Files,
  CornerUpRight,
} from 'lucide-react';
import jsPDF from 'jspdf';

const AINA_PENDING_EDIT_STORAGE_KEY = 'aina_pending_edit';
import { API } from '@/lib/api';
import { readEventStream } from '@/lib/sse';
import {
  buildStructuredMarkdownSections,
  markdownToHtml,
  markdownToPlainText,
  parseMarkdownBlocks,
  stripInlineMarkdown,
} from '@/lib/markdownExport';
import {
  countWords,
  getSectionTitle,
  normalizeStructuredContent,
  structuredSectionsToMarkdown,
} from '@/lib/templateWorkspace';
import { fetchAgentWorkspace } from '@/lib/agentWorkspace';
import { DRAFTING_MODE_OPTIONS, inferDraftingMode, getDraftingModeMeta } from '@/lib/draftingModes';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const SOURCE_STATUS_META = {
  PRESCRIBED_FORM: {
    label: 'Prescribed Form',
    description: 'Built from an official Government of India or Supreme Court filing form.',
    accent: '#4ade80',
    glow: 'rgba(74, 222, 128, 0.16)',
  },
  COURT_GUIDED: {
    label: 'Court Guided',
    description: 'Structured from official court filing materials and checklists.',
    accent: '#60a5fa',
    glow: 'rgba(96, 165, 250, 0.16)',
  },
  STATUTE_GUIDED: {
    label: 'Statute Guided',
    description: 'No prescribed government form exists, so the layout is anchored to official statutes only.',
    accent: '#f59e0b',
    glow: 'rgba(245, 158, 11, 0.16)',
  },
  DEFAULT: {
    label: 'Structured Draft',
    description: 'Structured drafting workspace.',
    accent: 'var(--accent-primary)',
    glow: 'var(--accent-glow)',
  },
};

const SUPPORT_LABEL_META = {
  'source-backed': {
    label: 'Source-backed',
    accent: '#0f9f53',
    background: 'rgba(15, 159, 83, 0.12)',
  },
  'matter-backed': {
    label: 'Matter-backed',
    accent: '#2563eb',
    background: 'rgba(37, 99, 235, 0.12)',
  },
  inferred: {
    label: 'Inferred',
    accent: '#b45309',
    background: 'rgba(180, 83, 9, 0.12)',
  },
  unsupported: {
    label: 'Unsupported',
    accent: '#b91c1c',
    background: 'rgba(185, 28, 28, 0.12)',
  },
};

const FINDING_SEVERITY_META = {
  high: {
    label: 'High',
    accent: '#b91c1c',
    background: 'rgba(185, 28, 28, 0.12)',
  },
  medium: {
    label: 'Medium',
    accent: '#b45309',
    background: 'rgba(180, 83, 9, 0.12)',
  },
  low: {
    label: 'Low',
    accent: '#475569',
    background: 'rgba(71, 85, 105, 0.12)',
  },
};

const TEMPLATE_SUGGESTIONS = [
  'Review this draft against the official structure and point out what is missing.',
  'Improve the wording of the active section without changing its heading structure.',
  'List the facts or details I still need before this template is filing-ready.',
  'Rewrite this section in tighter, court-ready language.',
  'Check whether the clause order still follows the template properly.',
];

const GENERAL_SUGGESTIONS = [
  'Complete this document',
  'Add missing clauses',
  'Check for legal issues',
  'Simplify the language',
  'Improve the structure',
];

const MASTER_DOC_RESEARCH_SUGGESTIONS = [
  'Review this section against the current research brief and identify what is unsupported.',
  'Which binding or persuasive authorities are still missing for this draft?',
  'Draft the next section using the selected sources and mark any factual gaps clearly.',
  'Cross-check the current claims against the research memo and point out overstatements.',
  'Tell me what the judge is most likely to question in this section.',
];

const MASTER_DOC_DRAFT_SUGGESTIONS = [
  'Tighten the active section without changing the legal meaning.',
  'Rewrite this section in more court-ready language.',
  'Improve the structure of the current draft for readability and sequence.',
  'List the missing factual inputs before this section is filing-ready.',
  'Turn the current notes into a cleaner drafted section.',
];

const AUTHORITY_ACTIONS = [
  {
    key: 'strengthen_section',
    label: 'Strengthen active section',
    helper: 'Use this authority to reinforce the current section.',
  },
  {
    key: 'check_support',
    label: 'Check support for claim',
    helper: 'Test whether the authority really supports the section as drafted.',
  },
  {
    key: 'explain_relevance',
    label: 'Explain relevance',
    helper: 'Show how it fits the present drafting posture and research strategy.',
  },
];

const ISSUE_ACTIONS = [
  {
    key: 'strengthen_issue',
    label: 'Strengthen on this issue',
    helper: 'Improve the active section around this issue.',
  },
  {
    key: 'find_missing_support',
    label: 'Find missing support',
    helper: 'Identify facts, authorities, or reasoning still needed.',
  },
  {
    key: 'stress_test_issue',
    label: 'Stress-test this issue',
    helper: 'Pressure-test the section and surface weaknesses.',
  },
];

function createCustomSection(index) {
  return {
    id: `custom_${Date.now()}_${index}`,
    editor_label: `Custom Section ${index}`,
    document_heading: `Custom Section ${index}`,
    body: '',
    guidance: 'Use custom sections sparingly so the official or statute-guided structure stays intact.',
    placeholders: [],
    level: 2,
    kind: 'body',
    locked_heading: false,
    required: false,
  };
}

const PDF_HEADING_SIZES = {
  1: 18,
  2: 15,
  3: 13,
  4: 12,
  5: 11,
  6: 11,
};

function escapeHtmlText(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function normalizeEditorSectionsForAgent({ isStructuredWorkspace, sections, activeSection, title, content }) {
  if (isStructuredWorkspace) {
    return (sections || []).map(section => ({
      id: section.id,
      title: getSectionTitle(section),
      content: section.body || '',
      is_active: Boolean(activeSection?.id && activeSection.id === section.id),
    }));
  }
  return [{
    id: 'document_body',
    title: title || 'Document',
    content: content || '',
    is_active: true,
  }];
}

function formatSourceFilterLabel(value) {
  const normalized = String(value || 'all').toLowerCase();
  if (normalized === 'sc') return 'Supreme Court focus';
  if (normalized === 'hc') return 'High Court focus';
  if (normalized === 'tribunal') return 'Tribunal focus';
  if (normalized === 'gov') return 'Government sources focus';
  return 'All verified sources';
}

function formatWorkflowModeLabel(value) {
  return String(value || '')
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Draft workspace';
}

function formatResearchTypeLabel(value) {
  return String(value || '')
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Research';
}

function formatCourtScopeLabel(value) {
  const normalized = String(value || '').toUpperCase();
  if (normalized === 'SC') return 'Supreme Court';
  if (normalized === 'HC') return 'High Court';
  if (normalized === 'TRIBUNAL') return 'Tribunal';
  if (normalized === 'TRIAL') return 'Trial / Sessions';
  return 'Any forum';
}

function compactText(value, maxLength = 180) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  return text.length > maxLength ? `${text.slice(0, maxLength).trim()}…` : text;
}

function encodeFocusCue(value) {
  return String(value || '').trim();
}

function formatRouterLabel(value) {
  return String(value || '')
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Not set';
}

function ActivityRouterSection({ section }) {
  const cards = [
    { label: 'Current Activity', value: formatRouterLabel(section.activity_type) },
    { label: 'Recommended Next Desk', value: formatRouterLabel(section.recommended_agent || section.suggested_next) },
    { label: 'Confidence', value: formatRouterLabel(section.activity_confidence) },
    { label: 'Most Recent Agent', value: formatRouterLabel(section.last_agent) },
  ].filter(item => item.value && item.value !== 'Not set');
  const summary = stripInlineMarkdown(section.context_summary || '');
  const reasoning = stripInlineMarkdown(section.activity_reasoning || section.body || '');

  return (
    <div className="space-y-4">
      <div
        className="rounded-[22px] p-4 sm:p-5"
        style={{
          background: 'var(--workspace-hero-bg)',
          border: '1px solid color-mix(in srgb, var(--accent-primary) 18%, var(--workspace-surface-border))',
          boxShadow: 'var(--workspace-surface-shadow)',
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          {cards.map(card => (
            <div
              key={card.label}
              className="rounded-2xl px-4 py-3"
              style={{ background: 'var(--workspace-surface-bg)', border: '1px solid var(--workspace-surface-border)' }}
            >
              <p className="text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
                {card.label}
              </p>
              <p className="mt-2 text-sm sm:text-base leading-6" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>
                {card.value}
              </p>
            </div>
          ))}
        </div>
        {(section.matter_name || summary) && (
          <div className="mt-4 flex flex-wrap gap-2">
            {section.matter_name && (
              <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-medium" style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}>
                Matter: {section.matter_name}
              </span>
            )}
            {summary && (
              <span className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium" style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}>
                {summary}
              </span>
            )}
          </div>
        )}
      </div>

      {reasoning && (
        <div
          className="rounded-2xl px-4 py-4"
          style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
        >
          <p className="text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: 'var(--text-muted)' }}>
            Routing Note
          </p>
          <p className="text-sm leading-7" style={{ color: 'var(--text-secondary)' }}>
            {reasoning}
          </p>
        </div>
      )}
    </div>
  );
}

function InlineMarkdownFragment({ text }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <>{children}</>,
      }}
    >
      {text}
    </ReactMarkdown>
  );
}

export function StructuredAssistantMessage({ content }) {
  const structuredContent = React.useMemo(
    () => buildStructuredMarkdownSections(content),
    [content],
  );

  const sections = structuredContent.sections?.length
    ? structuredContent.sections
    : (String(content || '').trim()
      ? [{
          heading: '',
          tone: 'default',
          blocks: [{ type: 'paragraph', text: String(content || '').trim() }],
        }]
      : []);

  if (!structuredContent.title && !sections.length) {
    return null;
  }

  return (
    <div className="markdown-content app-chat-structured" data-testid="document-assistant-structured-content">
      {structuredContent.title ? (
        <div className="app-chat-structured__title">{structuredContent.title}</div>
      ) : null}
      <div className="app-chat-structured__stack">
        {sections.map((section, sectionIndex) => (
          <section
            key={`${section.heading || 'section'}-${sectionIndex}`}
            className={`app-chat-structured-section app-chat-structured-section--${section.tone || 'default'}`}
          >
            {section.heading ? (
              <h3 className="app-chat-structured-section__heading">{section.heading}</h3>
            ) : null}
            <div className="app-chat-structured-section__body">
              {section.blocks.map((block, blockIndex) => {
                if (block.type === 'paragraph') {
                  return (
                    <div key={`paragraph-${blockIndex}`} className="app-chat-structured-paragraph">
                      <InlineMarkdownFragment text={block.text} />
                    </div>
                  );
                }

                if (block.type === 'list') {
                  return (
                    <ol
                      key={`list-${blockIndex}`}
                      className={`app-chat-structured-list ${block.ordered ? 'app-chat-structured-list--ordered' : 'app-chat-structured-list--unordered'}`}
                    >
                      {block.items.map((item, itemIndex) => (
                        <li key={`${item}-${itemIndex}`} className="app-chat-structured-list__item">
                          <span className="app-chat-structured-list__marker" aria-hidden="true">
                            {block.ordered ? itemIndex + 1 : ''}
                          </span>
                          <span className="app-chat-structured-list__content">
                            <InlineMarkdownFragment text={item} />
                          </span>
                        </li>
                      ))}
                    </ol>
                  );
                }

                if (block.type === 'code') {
                  return (
                    <pre key={`code-${blockIndex}`} className="app-chat-structured-code">
                      <code>{block.text}</code>
                    </pre>
                  );
                }

                return null;
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function renderMarkdownToPdf(pdf, markdown, { margin, pageWidth, pageHeight, startY }) {
  const blocks = parseMarkdownBlocks(markdown);
  const maxWidth = pageWidth - (2 * margin);
  let y = startY;

  const ensurePageSpace = (requiredHeight = 18) => {
    if (y + requiredHeight <= pageHeight - margin) return;
    pdf.addPage();
    y = margin;
  };

  const writeWrappedText = (text, options = {}) => {
    const {
      font = 'times',
      fontStyle = 'normal',
      fontSize = 11,
      indent = 0,
      lineHeight = 15,
      spaceBefore = 0,
      spaceAfter = 8,
    } = options;
    const cleanText = String(text || '').trim();
    if (!cleanText) return;

    const usableWidth = maxWidth - indent;
    const lines = pdf.splitTextToSize(cleanText, usableWidth);
    ensurePageSpace(spaceBefore + (lines.length * lineHeight) + spaceAfter);
    y += spaceBefore;
    pdf.setFont(font, fontStyle);
    pdf.setFontSize(fontSize);
    lines.forEach((line) => {
      ensurePageSpace(lineHeight);
      pdf.text(line, margin + indent, y);
      y += lineHeight;
    });
    y += spaceAfter;
  };

  blocks.forEach((block) => {
    if (block.type === 'heading') {
      const headingLevel = Math.min(Math.max(Number(block.level || 2), 1), 6);
      writeWrappedText(stripInlineMarkdown(block.text), {
        fontStyle: 'bold',
        fontSize: PDF_HEADING_SIZES[headingLevel] || 12,
        lineHeight: headingLevel <= 2 ? 18 : 16,
        spaceBefore: headingLevel <= 2 ? 10 : 6,
        spaceAfter: 6,
      });
      return;
    }

    if (block.type === 'paragraph') {
      writeWrappedText(stripInlineMarkdown(block.text), {
        fontSize: 11,
        lineHeight: 15,
        spaceAfter: 8,
      });
      return;
    }

    if (block.type === 'list') {
      block.items.forEach((item, index) => {
        const prefix = block.ordered ? `${index + 1}. ` : '\u2022 ';
        writeWrappedText(`${prefix}${stripInlineMarkdown(item)}`, {
          fontSize: 11,
          lineHeight: 15,
          indent: 14,
          spaceAfter: 4,
        });
      });
      y += 4;
      return;
    }

    if (block.type === 'code') {
      writeWrappedText(block.text, {
        font: 'courier',
        fontSize: 10,
        lineHeight: 14,
        spaceBefore: 4,
        spaceAfter: 8,
      });
    }
  });
}

export default function DocumentEditor() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getAuthHeaders } = useAuth();
  const [doc, setDoc] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [structuredContent, setStructuredContent] = useState(null);
  const [draftingMode, setDraftingMode] = useState('written_submission');
  const [draftingSupport, setDraftingSupport] = useState(null);
  const [draftingSupportLoading, setDraftingSupportLoading] = useState(false);
  const [draftingSupportError, setDraftingSupportError] = useState('');
  const [viewMode, setViewMode] = useState('raw');
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(true);
  const [showAssistant, setShowAssistant] = useState(false);
  const [assistantMode, setAssistantMode] = useState('research');
  const [showWorkspaceContext, setShowWorkspaceContext] = useState(false);
  const [activeContextMenu, setActiveContextMenu] = useState(null);
  const [aiInput, setAiInput] = useState('');
  const [aiMessages, setAiMessages] = useState([]);
  const [aiStreaming, setAiStreaming] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const autosaveReadyRef = useRef(false);
  const sectionRefs = useRef({});
  const aiEndRef = useRef(null);
  const appendAiDictation = useCallback((text) => {
    const cleaned = String(text || '').trim();
    if (!cleaned) return;
    setAiInput(prev => {
      const spacer = prev && !/[ \n]$/.test(prev) ? ' ' : '';
      return `${prev}${spacer}${cleaned}`.trimStart();
    });
  }, []);

  useEffect(() => {
    if (docId) fetchDocument();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  const fetchWorkspace = useCallback(async (workspaceId) => {
    if (!workspaceId) {
      setWorkspace(null);
      return null;
    }
    try {
      const nextWorkspace = await fetchAgentWorkspace({
        workspaceId,
        headers: getAuthHeaders(),
      });
      setWorkspace(nextWorkspace);
      setAssistantMode(nextWorkspace?.recommended_agent === 'CHAT_AGENT' ? 'draft' : 'research');
      return nextWorkspace;
    } catch {
      setWorkspace(null);
      return null;
    }
  }, [getAuthHeaders]);

  const fetchDraftingSupport = useCallback(async ({
    nextDoc = doc,
    nextContent = content,
    nextStructuredContent = structuredContent,
    nextMode = draftingMode,
  } = {}) => {
    if (!docId || !nextDoc) return null;
    setDraftingSupportLoading(true);
    setDraftingSupportError('');
    try {
      const resp = await fetch(`${API}/documents/${docId}/drafting-support`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          content: nextContent,
          structured_content: nextStructuredContent,
          drafting_mode: nextMode,
          workspace_id: nextDoc.workspace_id || location.state?.workspaceId || null,
          matter_id: nextDoc.matter_context?.id || null,
          persist_mode: true,
        }),
      });
      if (!resp.ok) throw new Error('support failed');
      const payload = await resp.json();
      setDraftingSupport(payload);
      return payload;
    } catch {
      setDraftingSupport(null);
      setDraftingSupportError('Draft review is unavailable right now.');
      return null;
    } finally {
      setDraftingSupportLoading(false);
    }
  }, [content, doc, docId, draftingMode, getAuthHeaders, location.state?.workspaceId, structuredContent]);

  const fetchDocument = async () => {
    autosaveReadyRef.current = false;
    try {
      const resp = await fetch(`${API}/documents/${docId}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!resp.ok) return;
      const nextDoc = await resp.json();
      const nextStructured = normalizeStructuredContent(nextDoc);
      setDoc(nextDoc);
      setTitle(nextDoc.title || '');
      setContent(nextDoc.content || '');
      setStructuredContent(nextStructured);
      setDraftingMode(inferDraftingMode(nextDoc));
      setViewMode(nextStructured?.sections?.length ? 'structured' : 'raw');
      setActiveSectionId(nextStructured?.sections?.[0]?.id || null);
      setShowAssistant(false);
      setSaved(true);
      if (nextDoc.workspace_id) {
        await fetchWorkspace(nextDoc.workspace_id);
      } else {
        setWorkspace(null);
      }
      await fetchDraftingSupport({
        nextDoc,
        nextContent: nextDoc.content || '',
        nextStructuredContent: nextStructured,
        nextMode: inferDraftingMode(nextDoc),
      });
    } catch {}
  };

  const isStructuredWorkspace = Boolean(structuredContent?.sections?.length);
  const sections = structuredContent?.sections || [];
  const activeSection = sections.find(section => section.id === activeSectionId) || sections[0] || null;
  const templateSnapshot = doc?.template_snapshot || null;
  const officialReferences = templateSnapshot?.official_references || [];
  const sourceStatus = SOURCE_STATUS_META[templateSnapshot?.source_status] || SOURCE_STATUS_META.DEFAULT;
  const currentContent = isStructuredWorkspace ? structuredSectionsToMarkdown(sections) : content;
  const wordCount = countWords(currentContent);
  const workspaceResearchState = workspace?.research_state || {};
  const workspaceActiveSources = workspaceResearchState.active_sources || workspaceResearchState.kickoff_cases || [];
  const workspaceActiveUserDocIds = workspaceResearchState.active_user_doc_ids || [];
  const workspacePinnedSources = workspaceResearchState.pinned_sources || [];
  const workspaceSourceFilter = workspaceResearchState.source_filter || 'all';
  const workspaceResearchBrief = workspaceResearchState.kickoff_report || '';
  const workspaceResearchSections = workspaceResearchState.document_sections || [];
  const workspaceIssues = workspaceResearchState.decomposed_issues || [];
  const workspaceQuery = workspaceResearchState.query || '';
  const workspacePostureSummary = compactText(
    workspaceResearchBrief
    || workspaceQuery
    || workspace?.overview_markdown
    || '',
    180,
  );
  const workspaceTemplateLabel = templateSnapshot?.title
    || formatWorkflowModeLabel(workspaceResearchState.workflow_mode || workspaceResearchState.template_type);
  const workspaceScopeLabel = formatCourtScopeLabel(workspaceResearchState.primary_court);
  const workspaceFilterLabel = formatSourceFilterLabel(workspaceSourceFilter);
  const compactAuthorityList = workspaceActiveSources.slice(0, 3);
  const compactPinnedList = workspacePinnedSources.slice(0, 4);
  const compactIssueList = workspaceIssues.slice(0, 4);
  const quickSuggestions = workspace?.workspace_id
    ? (assistantMode === 'research' ? MASTER_DOC_RESEARCH_SUGGESTIONS : MASTER_DOC_DRAFT_SUGGESTIONS)
    : (isStructuredWorkspace ? TEMPLATE_SUGGESTIONS : GENERAL_SUGGESTIONS);
  const assistantTitle = workspace?.workspace_id
    ? (assistantMode === 'research' ? 'Research Counsel' : 'Drafting Counsel')
    : (isStructuredWorkspace ? 'Drafting Counsel' : 'AI Assistant');
  const assistantSubtitle = workspace?.workspace_id
    ? (assistantMode === 'research'
      ? `${workspaceActiveSources.length} research source${workspaceActiveSources.length === 1 ? '' : 's'} · ${workspaceActiveUserDocIds.length} context doc${workspaceActiveUserDocIds.length === 1 ? '' : 's'}`
      : `Live drafting on ${activeSection ? getSectionTitle(activeSection) : 'the current document'}`)
    : (isStructuredWorkspace
      ? 'Working against the template structure and official references.'
      : 'General drafting help for the current document.');
  const draftingModeMeta = draftingSupport?.mode || getDraftingModeMeta(draftingMode);
  const draftingModeOptions = draftingSupport?.mode_options || DRAFTING_MODE_OPTIONS;
  const reviewFindings = draftingSupport?.validation?.findings || [];
  const supportCounts = draftingSupport?.validation?.support_counts || {};
  const authorityInsertions = draftingSupport?.source_insertions?.authorities || [];
  const matterFactInsertions = draftingSupport?.source_insertions?.matter_facts || [];
  const trustSummary = draftingSupport?.trust_summary || {};

  const insertSupportNote = useCallback((text, label = 'support note') => {
    const cleaned = String(text || '').trim();
    if (!cleaned) return;
    if (isStructuredWorkspace && activeSection) {
      updateStructuredSection(activeSection.id, 'body', `${activeSection.body || ''}\n\n${cleaned}`.trim());
      toast.success(`${label} inserted into ${getSectionTitle(activeSection)}`);
      return;
    }
    setContent(prev => `${prev}\n\n${cleaned}`.trim());
    toast.success(`${label} inserted`);
  }, [activeSection, isStructuredWorkspace]);

  const copySupportNote = useCallback(async (text, label = 'support note') => {
    try {
      await navigator.clipboard.writeText(String(text || '').trim());
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Could not copy ${label}`);
    }
  }, []);

  const selectDraftingMode = useCallback(async (modeId) => {
    setDraftingMode(modeId);
    setDoc(prev => (prev ? { ...prev, drafting_mode: modeId } : prev));
    await fetchDraftingSupport({
      nextDoc: doc ? { ...doc, drafting_mode: modeId } : null,
      nextContent: currentContent,
      nextStructuredContent: structuredContent,
      nextMode: modeId,
    });
  }, [currentContent, doc, fetchDraftingSupport, structuredContent]);

  const saveDocument = useCallback(async () => {
    if (!docId) return;
    setSaving(true);
    try {
      const resp = await fetch(`${API}/documents/${docId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          title,
          content: currentContent,
          structured_content: isStructuredWorkspace ? structuredContent : null,
          drafting_mode: draftingMode,
        }),
      });
      if (resp.ok) {
        setSaved(true);
        const updated = await resp.json();
        setDoc(updated);
      } else {
        throw new Error('Save failed');
      }
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }, [docId, title, currentContent, isStructuredWorkspace, structuredContent, draftingMode, getAuthHeaders]);

  useEffect(() => {
    if (!doc?.doc_id) return;
    if (!autosaveReadyRef.current) {
      autosaveReadyRef.current = true;
      return;
    }
    setSaved(false);
    const timer = setTimeout(() => {
      saveDocument();
    }, 1800);
    return () => clearTimeout(timer);
  }, [doc?.doc_id, title, content, structuredContent, saveDocument]);

  const updateStructuredSection = (id, field, value) => {
    setStructuredContent(prev => ({
      ...(prev || { version: 2, template_id: doc?.parent_template_id || null }),
      sections: (prev?.sections || []).map(section =>
        section.id === id ? { ...section, [field]: value } : section
      ),
    }));
  };

  const moveSection = (index, direction) => {
    setStructuredContent(prev => {
      const currentSections = [...(prev?.sections || [])];
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= currentSections.length) return prev;
      const [section] = currentSections.splice(index, 1);
      currentSections.splice(nextIndex, 0, section);
      return { ...(prev || {}), sections: currentSections };
    });
  };

  const addCustomSection = () => {
    setStructuredContent(prev => {
      const currentSections = prev?.sections || [];
      const nextSection = createCustomSection(currentSections.length + 1);
      setActiveSectionId(nextSection.id);
      return {
        ...(prev || { version: 2, template_id: doc?.parent_template_id || null }),
        sections: [...currentSections, nextSection],
      };
    });
  };

  const handleStatusChange = async (status) => {
    try {
      await fetch(`${API}/documents/${docId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ status }),
      });
      setDoc(prev => ({ ...prev, status }));
      toast.success(`Status: ${status}`);
    } catch {}
  };

  const handleShare = () => {
    if (doc?.share_token) {
      navigator.clipboard.writeText(`${window.location.origin}/share/${doc.share_token}`);
      toast.success('Share link copied');
      handleStatusChange('SHARED');
    }
  };

  const exportPDF = () => {
    const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 50;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const exportText = currentContent;

    pdf.setFont('times', 'bold');
    pdf.setFontSize(16);
    pdf.text(title || 'Untitled Document', margin, margin + 18);

    pdf.setFont('times', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(110);
    pdf.text(
      `${(doc?.type || '').replace(/_/g, ' ')} | ${new Date().toLocaleDateString('en-IN')}`,
      margin,
      margin + 36
    );
    pdf.setTextColor(0);
    pdf.setDrawColor(210);
    pdf.line(margin, margin + 46, pageWidth - margin, margin + 46);

    renderMarkdownToPdf(pdf, exportText, {
      margin,
      pageWidth,
      pageHeight,
      startY: margin + 68,
    });

    pdf.save(`${title || 'document'}.pdf`);
    toast.success('PDF downloaded');
    setShowExportMenu(false);
  };

  const exportDOCX = () => {
    const htmlBody = markdownToHtml(currentContent);
    const safeTitle = escapeHtmlText(title || 'Untitled Document');
    const fallbackPlainText = escapeHtmlText(markdownToPlainText(currentContent));
    const htmlContent = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${safeTitle}</title>
<style>
body{font-family:'Times New Roman',serif;font-size:12pt;line-height:1.6;margin:1in;color:#111827;}
h1{font-size:18pt;margin:0 0 10pt;}
h2{font-size:15pt;margin:18pt 0 8pt;}
h3{font-size:13pt;margin:16pt 0 6pt;}
p{margin:0 0 10pt;}
ul,ol{margin:0 0 10pt 18pt;padding:0;}
li{margin:0 0 4pt;}
code{font-family:'Courier New',monospace;font-size:10pt;}
pre{font-family:'Courier New',monospace;font-size:10pt;white-space:pre-wrap;border:1px solid #d1d5db;padding:10pt;border-radius:6pt;}
a{color:#1f6a49;text-decoration:none;}
</style>
</head><body><h1>${safeTitle}</h1>${htmlBody || `<p>${fallbackPlainText}</p>`}</body></html>`;
    const blob = new Blob([htmlContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${title || 'document'}.doc`;
    anchor.click();
    URL.revokeObjectURL(url);
    toast.success('Document downloaded (.doc)');
    setShowExportMenu(false);
  };

  const exportTXT = () => {
    const plainText = markdownToPlainText(currentContent);
    const blob = new Blob([plainText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${title || 'document'}.txt`;
    anchor.click();
    URL.revokeObjectURL(url);
    toast.success('Text file downloaded');
    setShowExportMenu(false);
  };

  const handleBackNavigation = useCallback(() => {
    const explicitSearch = String(location.state?.researchSearch || '').trim();
    const existingSearch = String(location.search || '').trim();
    const returnSearch = explicitSearch
      ? `?${explicitSearch}`
      : (existingSearch.includes('workspace_id=') ? existingSearch : '');

    if (returnSearch) {
      navigate(`/research${returnSearch}`);
      return;
    }

    const params = new URLSearchParams();
    const workspaceId = location.state?.workspaceId || doc?.workspace_id || workspace?.workspace_id;
    if (workspaceId) params.set('workspace_id', workspaceId);

    const linkedDoc = workspace?.linked_doc || doc?.linked_doc;
    if (linkedDoc?.id && linkedDoc?.type) {
      params.set('linked_doc_id', linkedDoc.id);
      params.set('linked_doc_type', linkedDoc.type);
      params.set('linked_doc_title', linkedDoc.title || 'Linked document');
    }

    const matterContext = workspace?.matter_context || doc?.matter_context;
    if (matterContext?.id || matterContext?.name) {
      params.set('matter_id', matterContext?.id || matterContext?.name);
      params.set('matter_name', matterContext?.name || matterContext?.id);
    }

    if (params.toString()) {
      navigate(`/research?${params.toString()}`);
      return;
    }

    navigate('/documents');
  }, [doc?.linked_doc, doc?.matter_context, doc?.workspace_id, location.search, location.state?.researchSearch, location.state?.workspaceId, navigate, workspace]);

  const openLinkedResearchWorkspace = useCallback(() => {
    const explicitSearch = String(location.state?.researchSearch || '').trim();
    const existingSearch = String(location.search || '').trim();
    if (explicitSearch) {
      navigate(`/research?${explicitSearch}`);
      return;
    }
    if (existingSearch.includes('workspace_id=')) {
      navigate(`/research${existingSearch}`);
      return;
    }

    const params = new URLSearchParams();
    const workspaceId = workspace?.workspace_id || doc?.workspace_id || location.state?.workspaceId;
    if (workspaceId) params.set('workspace_id', workspaceId);
    const linkedDoc = workspace?.linked_doc || doc?.linked_doc;
    if (linkedDoc?.id && linkedDoc?.type) {
      params.set('linked_doc_id', linkedDoc.id);
      params.set('linked_doc_type', linkedDoc.type);
      params.set('linked_doc_title', linkedDoc.title || 'Linked document');
    }
    const matterContext = workspace?.matter_context || doc?.matter_context;
    if (matterContext?.id || matterContext?.name) {
      params.set('matter_id', matterContext?.id || matterContext?.name);
      params.set('matter_name', matterContext?.name || matterContext?.id);
    }
    navigate(params.toString() ? `/research?${params.toString()}` : '/research');
  }, [doc?.linked_doc, doc?.matter_context, doc?.workspace_id, location.search, location.state?.researchSearch, location.state?.workspaceId, navigate, workspace]);

  const refreshLinkedResearchWorkspace = useCallback(async () => {
    if (!doc?.workspace_id && !workspace?.workspace_id) return;
    const nextWorkspace = await fetchWorkspace(doc?.workspace_id || workspace?.workspace_id);
    if (nextWorkspace) {
      toast.success('Drafting posture refreshed from Research');
    } else {
      toast.error('Could not refresh linked Research context');
    }
  }, [doc?.workspace_id, fetchWorkspace, workspace?.workspace_id]);

  const askAssistantWithCurrentContext = useCallback(() => {
    setShowAssistant(true);
    setAssistantMode('research');
    setAiInput(`Check the active section against the current research posture, authorities, and brief. Tell me what is missing or overstated.`);
  }, []);

  const buildAssistantFocusPrompt = useCallback((kind, value, metadata = {}, variant = null) => {
    const activeSectionTitle = activeSection ? getSectionTitle(activeSection) : 'the current document';
    const activeSectionBody = compactText(
      isStructuredWorkspace ? activeSection?.body : content,
      280,
    );
    const postureLine = workspacePostureSummary || compactText(workspaceResearchBrief || workspaceQuery || '', 180);
    const sharedContext = [
      workspace?.title ? `Linked Research workspace: ${workspace.title}` : '',
      workspaceQuery ? `Research query: ${workspaceQuery}` : '',
      `Draft posture: ${workspaceTemplateLabel}`,
      `Forum scope: ${workspaceScopeLabel}`,
      `Source scope: ${workspaceFilterLabel}`,
      `Context in play: ${workspaceActiveSources.length} selected authorities and ${workspaceActiveUserDocIds.length} selected context documents`,
      activeSectionTitle ? `Active section: ${activeSectionTitle}` : '',
      activeSectionBody ? `Current section excerpt: ${activeSectionBody}` : '',
      postureLine ? `Current research posture summary: ${postureLine}` : '',
    ].filter(Boolean);

    if (kind === 'authority') {
      const authorityDescriptor = [
        metadata.title || value,
        metadata.docsource,
        metadata.publishdate,
      ].filter(Boolean).join(' · ');
      if (variant === 'check_support') {
        return [
          `Check whether this authority really supports the claim currently being made in ${activeSectionTitle}.`,
          `Authority in focus: ${authorityDescriptor}`,
          ...sharedContext,
          'Tell me where the current section may be overstating the authority, what the real support is, and what qualifier or tighter wording should be used instead.',
        ].join('\n');
      }
      if (variant === 'explain_relevance') {
        return [
          `Explain how this authority fits the current research posture and drafting strategy for ${activeSectionTitle}.`,
          `Authority in focus: ${authorityDescriptor}`,
          ...sharedContext,
          'Tell me why this authority matters here, what proposition it is best used for, and where in the draft it should do the most work.',
        ].join('\n');
      }
      return [
        `Use this authority to strengthen ${activeSectionTitle}.`,
        `Authority in focus: ${authorityDescriptor}`,
        ...sharedContext,
        'Show me how to use this authority to strengthen the active section, improve the reasoning, and sharpen the legal proposition without over-claiming.',
      ].join('\n');
    }

    if (variant === 'find_missing_support') {
      return [
        `Find what support is still missing on this issue for ${activeSectionTitle}.`,
        `Issue in focus: ${value}`,
        ...sharedContext,
        'Tell me which facts, authorities, or reasoning are still missing on this issue before the section is safe and persuasive.',
      ].join('\n');
    }
    if (variant === 'stress_test_issue') {
      return [
        `Stress-test the current drafting on this issue in ${activeSectionTitle}.`,
        `Issue in focus: ${value}`,
        ...sharedContext,
        'Challenge the active section on this issue, identify weak points or unanswered objections, and tell me how opposing counsel or the bench may push back.',
      ].join('\n');
    }

    return [
      `Work through this issue in the context of ${activeSectionTitle}.`,
      `Issue in focus: ${value}`,
      ...sharedContext,
      'Tell me how to strengthen the active section on this issue, what the strongest framing is, and how the reasoning should be improved.',
    ].join('\n');
  }, [
    activeSection,
    content,
    isStructuredWorkspace,
    workspace?.title,
    workspaceActiveSources.length,
    workspaceActiveUserDocIds.length,
    workspaceFilterLabel,
    workspacePostureSummary,
    workspaceQuery,
    workspaceResearchBrief,
    workspaceScopeLabel,
    workspaceTemplateLabel,
  ]);

  const focusAuthorityInAssistant = useCallback((authority, variant = 'strengthen_section') => {
    setShowAssistant(true);
    setAssistantMode('research');
    setAiInput(buildAssistantFocusPrompt('authority', authority?.title || '', authority || {}, variant));
    setActiveContextMenu(null);
  }, [buildAssistantFocusPrompt]);

  const focusIssueInAssistant = useCallback((issue, variant = 'strengthen_issue') => {
    setShowAssistant(true);
    setAssistantMode('research');
    setAiInput(buildAssistantFocusPrompt('issue', issue, {}, variant));
    setActiveContextMenu(null);
  }, [buildAssistantFocusPrompt]);

  const copyPostureSummary = useCallback(async () => {
    const lines = [
      workspace?.title ? `Research workspace: ${workspace.title}` : '',
      workspaceQuery ? `Research query: ${workspaceQuery}` : '',
      `Draft posture: ${workspaceTemplateLabel}`,
      `Authority set: ${workspaceActiveSources.length} selected, ${workspacePinnedSources.length} pinned`,
      `Context docs: ${workspaceActiveUserDocIds.length}`,
      `Forum scope: ${workspaceScopeLabel}`,
      `Source filter: ${workspaceFilterLabel}`,
      workspacePostureSummary ? `Posture summary: ${workspacePostureSummary}` : '',
    ].filter(Boolean).join('\n');
    try {
      await navigator.clipboard.writeText(lines);
      toast.success('Drafting posture copied');
    } catch {
      toast.error('Could not copy posture summary');
    }
  }, [
    workspace?.title,
    workspaceActiveSources.length,
    workspaceActiveUserDocIds.length,
    workspaceFilterLabel,
    workspacePinnedSources.length,
    workspacePostureSummary,
    workspaceQuery,
    workspaceScopeLabel,
    workspaceTemplateLabel,
  ]);

  const openResearchWithFocusCue = useCallback(({ authority = '', issue = '' }) => {
    const params = new URLSearchParams();
    const workspaceId = workspace?.workspace_id || doc?.workspace_id || location.state?.workspaceId;
    if (workspaceId) params.set('workspace_id', workspaceId);

    const linkedDoc = workspace?.linked_doc || doc?.linked_doc;
    if (linkedDoc?.id && linkedDoc?.type) {
      params.set('linked_doc_id', linkedDoc.id);
      params.set('linked_doc_type', linkedDoc.type);
      params.set('linked_doc_title', linkedDoc.title || 'Linked document');
    }

    const matterContext = workspace?.matter_context || doc?.matter_context;
    if (matterContext?.id || matterContext?.name) {
      params.set('matter_id', matterContext?.id || matterContext?.name);
      params.set('matter_name', matterContext?.name || matterContext?.id);
    }

    if (workspaceResearchState.research_type) {
      params.set('prefill_type', workspaceResearchState.research_type);
    }
    if (authority) params.set('focus_authority', encodeFocusCue(authority));
    if (issue) params.set('focus_issue', encodeFocusCue(issue));

    navigate(`/research?${params.toString()}`);
  }, [
    doc?.linked_doc,
    doc?.matter_context,
    doc?.workspace_id,
    location.state?.workspaceId,
    navigate,
    workspace,
    workspaceResearchState.research_type,
  ]);

  const renderContextActionPopover = useCallback(({ kind, item, trigger, itemKey }) => {
    const menuKey = kind === 'authority'
      ? `authority:${item?.id || item?.url || item?.title}`
      : `issue:${item}`;
    const actions = kind === 'authority' ? AUTHORITY_ACTIONS : ISSUE_ACTIONS;
    const title = kind === 'authority' ? compactText(item?.title, 56) : compactText(item, 56);
    const subtitle = kind === 'authority'
      ? [item?.docsource, item?.publishdate].filter(Boolean).join(' · ')
      : activeSection ? `Active section: ${getSectionTitle(activeSection)}` : 'Use this issue in the current draft';

    return (
      <Popover
        key={itemKey || menuKey}
        open={activeContextMenu === menuKey}
        onOpenChange={(open) => setActiveContextMenu(open ? menuKey : null)}
      >
        <PopoverTrigger asChild>
          {trigger}
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={8}
          className="w-[320px] rounded-[20px] p-0"
          style={{
            background: 'var(--workspace-canvas-card-bg)',
            border: '1px solid var(--workspace-surface-border)',
            boxShadow: 'var(--workspace-surface-shadow)',
            color: 'var(--text-primary)',
          }}
        >
          <div className="px-4 py-4" style={{ borderBottom: '1px solid var(--workspace-surface-border)' }}>
            <p className="text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
              {kind === 'authority' ? 'Authority actions' : 'Issue actions'}
            </p>
            <p className="mt-2 text-sm leading-6" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>
              {title}
            </p>
            {subtitle && (
              <p className="mt-1 text-[11px] leading-5" style={{ color: 'var(--text-muted)' }}>
                {subtitle}
              </p>
            )}
          </div>
          <div className="p-2">
            {actions.map(action => (
              <button
                key={action.key}
                type="button"
                onClick={() => (
                  kind === 'authority'
                    ? focusAuthorityInAssistant(item, action.key)
                    : focusIssueInAssistant(item, action.key)
                )}
                className="w-full rounded-[16px] px-3 py-3 text-left transition-colors"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-primary)',
                }}
              >
                <p className="text-[12px] font-medium">{action.label}</p>
                <p className="text-[11px] mt-1 leading-5" style={{ color: 'var(--text-muted)' }}>
                  {action.helper}
                </p>
              </button>
            ))}
          </div>
          <div className="px-3 pb-3 pt-1" style={{ borderTop: '1px solid var(--workspace-surface-border)' }}>
            <button
              type="button"
              onClick={() => {
                openResearchWithFocusCue(
                  kind === 'authority'
                    ? { authority: item?.title || '' }
                    : { issue: item || '' }
                );
                setActiveContextMenu(null);
              }}
              className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-medium"
              style={{
                background: 'var(--workspace-chip-bg)',
                border: '1px solid var(--workspace-surface-border)',
                color: 'var(--text-secondary)',
              }}
            >
              <CornerUpRight size={12} />
              Open in Research
            </button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }, [activeContextMenu, activeSection, focusAuthorityInAssistant, focusIssueInAssistant, openResearchWithFocusCue]);

  const sendAiMessage = async () => {
    if (!aiInput.trim() || aiStreaming) return;

    const userMsg = { role: 'user', content: aiInput, agent: assistantMode };
    const asstMsg = { role: 'assistant', content: '', isStreaming: true, agent: assistantMode };
    setAiMessages(prev => [...prev, userMsg, asstMsg]);
    setAiInput('');
    setAiStreaming(true);

    try {
      const activeSectionBody = isStructuredWorkspace
        ? (activeSection?.body || '')
        : content;
      const documentSectionsForAgent = normalizeEditorSectionsForAgent({
        isStructuredWorkspace,
        sections,
        activeSection,
        title,
        content,
      });
      const researchOutline = workspaceResearchSections
        .filter(section => (section?.title || section?.document_heading) && section?.content)
        .slice(0, 8)
        .map(section => `${section.title || section.document_heading}: ${(section.content || '').slice(0, 320)}`)
        .join('\n');
      const contextualMessage = [
        assistantMode === 'research'
          ? 'You are working inside the Master Doc with the linked research workspace. Ground your answer in the selected authorities, research brief, and current draft.'
          : 'You are helping draft inside the Master Doc. Improve structure and language without inventing unsupported legal or factual claims.',
        activeSection
          ? `Active master-doc section: ${getSectionTitle(activeSection)}\nCurrent section body:\n${activeSectionBody.slice(0, 2500)}`
          : `Current document title: ${title}`,
        workspaceResearchBrief
          ? `Originating research brief:\n${workspaceResearchBrief.slice(0, 2600)}`
          : '',
        researchOutline
          ? `Originating research draft outline:\n${researchOutline}`
          : '',
        `User request: ${aiInput}`,
      ].filter(Boolean).join('\n\n');

      const messages = [
        ...aiMessages
          .filter(message => !message.isStreaming)
          .slice(-10)
          .map(message => ({
            role: message.role,
            content: message.content,
          })),
        { role: 'user', content: contextualMessage },
      ];

      const linkedDoc = workspace?.linked_doc || doc?.linked_doc;
      const matterContext = workspace?.matter_context || doc?.matter_context;
      const request = assistantMode === 'research' && workspace?.workspace_id
        ? fetch(`${API}/research/agent/stream`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
              message: contextualMessage,
              document_title: title,
              document_sections: documentSectionsForAgent,
              active_sources: workspaceActiveSources,
              sources: workspaceResearchState.sources || [],
              pinned_sources: workspacePinnedSources,
              source_filter: workspaceSourceFilter,
              sources_query: workspaceResearchState.sources_query || workspaceResearchState.query || title,
              active_user_doc_ids: workspaceActiveUserDocIds,
              history: aiMessages
                .filter(message => !message.isStreaming)
                .slice(-10)
                .map(message => ({ role: message.role, content: message.content })),
              template_type: templateSnapshot?.template_id || 'MASTER_DOC',
              research_type: workspaceResearchState.research_type,
              workflow_mode: workspaceResearchState.workflow_mode,
              primary_court: workspaceResearchState.primary_court,
              workspace_id: workspace.workspace_id,
              linked_doc_id: linkedDoc?.id || null,
              linked_doc_type: linkedDoc?.type || null,
              linked_doc_title: linkedDoc?.title || null,
              matter_id: matterContext?.id || null,
              matter_name: matterContext?.name || null,
            }),
          })
        : fetch(`${API}/chat/stream`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
              messages,
              mode: 'DRAFT',
              language: user?.language || 'ENGLISH',
              workspace_id: workspace?.workspace_id || doc?.workspace_id || null,
              linked_doc_id: docId,
              linked_doc_type: 'document',
              linked_doc_title: title,
              matter_id: matterContext?.id || null,
              matter_name: matterContext?.name || null,
            }),
          });

      const resp = await request;

      if (!resp.ok) throw new Error('Failed');
      let fullText = '';

      await readEventStream(resp, async (data) => {
        if (data.type === 'text') {
          fullText += data.text;
          setAiMessages(prev => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role === 'assistant') {
              next[next.length - 1] = { ...last, content: fullText };
            }
            return next;
          });
        }
        if (data.type === 'done' || data.type === 'citations') {
          setAiMessages(prev => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last) next[next.length - 1] = { ...last, isStreaming: false };
            return next;
          });
        }
      });
    } catch {
      toast.error('Assistant error');
      setAiMessages(prev => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.role === 'assistant') {
          next[next.length - 1] = {
            ...last,
            content: 'I hit an error while drafting. Please try again.',
            isStreaming: false,
          };
        }
        return next;
      });
    } finally {
      setAiStreaming(false);
    }
  };

  const applyAiSuggestion = (text) => {
    if (isStructuredWorkspace && activeSection) {
      updateStructuredSection(activeSection.id, 'body', `${activeSection.body || ''}\n\n${text}`.trim());
      toast.success(`Applied to ${getSectionTitle(activeSection)}`);
      return;
    }
    setContent(prev => `${prev}\n\n${text}`.trim());
    toast.success('Applied to document');
  };

  useEffect(() => {
    const handler = (event) => {
      const patch = String(event?.detail?.patch || '').trim();
      if (!patch) return;
      applyAiSuggestion(patch);
      try {
        window.localStorage.removeItem(AINA_PENDING_EDIT_STORAGE_KEY);
      } catch {}
    };
    window.addEventListener('aina-apply-edit', handler);
    return () => window.removeEventListener('aina-apply-edit', handler);
  }, [activeSection, content, isStructuredWorkspace, structuredContent]);

  useEffect(() => {
    if (!doc) return;
    try {
      const raw = window.localStorage.getItem(AINA_PENDING_EDIT_STORAGE_KEY);
      if (!raw) return;
      const pending = JSON.parse(raw);
      const pendingPatch = String(pending?.patch || '').trim();
      const pendingDocId = String(pending?.doc_id || '').trim();
      if (!pendingPatch) return;
      if (pendingDocId && pendingDocId !== docId) return;
      applyAiSuggestion(pendingPatch);
      window.localStorage.removeItem(AINA_PENDING_EDIT_STORAGE_KEY);
    } catch {}
  }, [doc, docId]);

  useEffect(() => {
    aiEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages]);

  const resolvedMatterContext = workspace?.matter_context || doc?.matter_context || null;
  const contextMatterId = resolvedMatterContext?.id || '';
  const contextMatterName = resolvedMatterContext?.name || contextMatterId || '';
  const contextWorkspaceId = workspace?.workspace_id || doc?.workspace_id || location.state?.workspaceId || '';
  const contextOverviewRoute = contextMatterId ? `/war-room/${contextMatterId}` : '/war-room';
  const contextDraftRoute = withQueryParams(`/documents/${docId}`, {
    matter_id: contextMatterId,
    matter_name: contextMatterName,
  });
  const contextFilesRoute = withQueryParams('/research', {
    workspace_id: contextWorkspaceId,
    matter_id: contextMatterId,
    matter_name: contextMatterName,
    context_panel: 'files',
  });
  const contextJollyRoute = withQueryParams('/jolly', {
    matter_id: contextMatterId,
    matter_name: contextMatterName,
  });
  const contextReviewRoute = contextMatterId ? `/war-room/${contextMatterId}?view=agents` : '/war-room?view=agents';
  const matterContextItems = contextMatterId ? [
    { key: 'overview', onClick: () => navigate(contextOverviewRoute) },
    { key: 'draft', badge: 'Live', onClick: () => navigate(contextDraftRoute) },
    { key: 'files', onClick: () => navigate(contextFilesRoute) },
    { key: 'jolly', onClick: () => navigate(contextJollyRoute) },
    { key: 'review', onClick: () => navigate(contextReviewRoute) },
  ] : [];

  if (!doc) {
    return (
      <div style={{ display: 'contents' }}>
        <main className="app-main">
          <div className="h-screen flex items-center justify-center" style={{ background: 'var(--bg-base)' }}>
            <Loader2 className="animate-spin" size={24} style={{ color: 'var(--text-secondary)' }} />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div data-testid="document-editor" style={{ display: 'contents' }}>
      <main className="app-main">
      <div className="h-screen flex flex-col workspace-shell" style={{ background: 'var(--workspace-shell-bg)' }}>
      <div
        className="flex items-center gap-2 px-4 py-2.5 shrink-0"
        style={{ borderBottom: '1px solid var(--workspace-surface-border)', background: 'var(--workspace-surface-bg)' }}
      >
        <button data-testid="back-to-docs" onClick={handleBackNavigation} className="p-1.5 rounded hover:opacity-70">
          <ArrowLeft size={16} style={{ color: 'var(--text-muted)' }} />
        </button>
        <input
          data-testid="doc-title-input"
          value={title}
          onChange={event => setTitle(event.target.value)}
          className="text-base font-semibold bg-transparent border-none outline-none flex-1 min-w-0"
          style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}
          placeholder="Document title..."
        />
        <span className="text-xs flex items-center gap-1 shrink-0" style={{ color: 'var(--text-muted)' }}>
          {saving ? <Loader2 size={11} className="animate-spin" /> : saved ? <Check size={11} /> : null}
          {saving ? 'Saving...' : saved ? 'Saved' : 'Autosave pending'}
        </span>
        {(workspace?.workspace_id || location.search.includes('workspace_id=')) && (
          <span className="text-[11px] shrink-0" style={{ color: 'var(--text-muted)' }}>
            Back to Research
          </span>
        )}

        {isStructuredWorkspace && (
          <button
            onClick={() => setViewMode(mode => (mode === 'structured' ? 'raw' : 'structured'))}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs"
            style={{
              border: '1px solid var(--border-subtle)',
              background: 'var(--bg-elevated)',
              color: 'var(--text-secondary)',
            }}
            title="Toggle between structured workspace and raw markdown view"
          >
            <Columns size={11} />
            {viewMode === 'structured' ? 'Raw View' : 'Structure'}
          </button>
        )}

        <select value={doc.status} onChange={event => handleStatusChange(event.target.value)} className="advoq-input text-xs py-1 px-1.5">
          <option value="DRAFT">Draft</option>
          <option value="FINAL">Final</option>
          <option value="SHARED">Shared</option>
          <option value="ARCHIVED">Archived</option>
        </select>
        <button onClick={handleShare} className="p-1.5 rounded hover:opacity-70" title="Share">
          <Share2 size={14} style={{ color: 'var(--text-muted)' }} />
        </button>

        <div className="relative">
          <button
            data-testid="export-btn"
            onClick={() => setShowExportMenu(open => !open)}
            className="p-1.5 rounded hover:opacity-70 flex items-center gap-1"
            title="Export"
            style={{ color: 'var(--text-muted)' }}
          >
            <Download size={14} />
            <span className="text-xs hidden sm:inline">Export</span>
          </button>
          {showExportMenu && (
            <div
              className="absolute right-0 top-full mt-1 z-50 rounded-lg py-1 min-w-[160px]"
              style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}
            >
              <button
                data-testid="export-pdf"
                onClick={exportPDF}
                className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors"
                style={{ color: 'var(--text-secondary)' }}
              >
                <FileDown size={13} />
                Export as PDF
              </button>
              <button
                data-testid="export-docx"
                onClick={exportDOCX}
                className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors"
                style={{ color: 'var(--text-secondary)' }}
              >
                <FileText size={13} />
                Export as Word (.doc)
              </button>
              <button
                data-testid="export-txt"
                onClick={exportTXT}
                className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors"
                style={{ color: 'var(--text-secondary)' }}
              >
                <FileText size={13} />
                Export as Text
              </button>
            </div>
          )}
        </div>

        <button
          onClick={() => setShowAssistant(open => !open)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-all duration-150"
          style={{
            background: showAssistant ? 'var(--bg-elevated)' : 'transparent',
            color: showAssistant ? 'var(--text-primary)' : 'var(--text-muted)',
            border: '1px solid var(--border-subtle)',
          }}
          data-testid="toggle-ai-assistant"
        >
          <MessageSquare size={13} />
          {isStructuredWorkspace ? 'Counsel' : 'AI Assistant'}
        </button>
        <button
          data-testid="save-doc-btn"
          onClick={saveDocument}
          className="px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition-all duration-150"
          style={{ background: 'var(--text-primary)', color: 'var(--bg-base)' }}
        >
          <Save size={12} />
          Save
        </button>
      </div>

      {contextMatterId ? (
        <div className="px-4 py-3 shrink-0" style={{ borderBottom: '1px solid var(--workspace-surface-border)', background: 'var(--workspace-shell-bg)' }}>
          <MatterWorkspaceContext
            matterName={contextMatterName}
            matterId={contextMatterId}
            workspaceId={contextWorkspaceId}
            objective={title || 'Continue drafting inside the matter.'}
            items={matterContextItems}
            activeKey="draft"
            compact
            caption="Matter context"
            testId="matter-workspace-context"
          />
        </div>
      ) : null}

      <div className="flex flex-1 min-h-0">
        {isStructuredWorkspace && viewMode === 'structured' && (
          <aside
            className="hidden xl:flex xl:w-[300px] shrink-0 flex-col"
            style={{ borderRight: '1px solid var(--workspace-surface-border)', background: 'var(--workspace-rail-bg)' }}
          >
            <div className="px-5 py-5 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
              <div
                className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-medium"
                style={{ background: sourceStatus.glow, color: sourceStatus.accent }}
              >
                <ShieldCheck size={12} />
                {sourceStatus.label}
              </div>
              <p className="mt-3 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>
                {templateSnapshot?.source_note || sourceStatus.description}
              </p>
            </div>

            <div className="px-5 py-5 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
              <div className="flex items-center gap-2 mb-3">
                <BookOpen size={14} style={{ color: 'var(--accent-primary)' }} />
                <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
                  Official References
                </h3>
              </div>
              <div className="space-y-3">
                {officialReferences.map((ref, index) => (
                  <a
                    key={`${ref.url}-${index}`}
                    href={ref.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block rounded-xl p-3"
                    style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{ref.title}</p>
                        <p className="text-[11px] mt-1" style={{ color: 'var(--text-secondary)' }}>{ref.authority}</p>
                      </div>
                      <ExternalLink size={12} style={{ color: 'var(--text-muted)' }} />
                    </div>
                    <p className="text-[11px] mt-2 leading-5" style={{ color: 'var(--text-muted)' }}>{ref.relevance}</p>
                  </a>
                ))}
              </div>
            </div>

            <div className="px-5 py-5 min-h-0 overflow-y-auto">
              <div className="flex items-center gap-2 mb-3">
                <LayoutList size={14} style={{ color: 'var(--accent-primary)' }} />
                <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
                  Structure
                </h3>
              </div>
              <div className="space-y-2">
                {sections.map((section, index) => {
                  const isActive = section.id === activeSection?.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => {
                        setActiveSectionId(section.id);
                        sectionRefs.current[section.id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      className="w-full rounded-xl p-3 text-left transition-all"
                      style={{
                        background: isActive ? sourceStatus.glow : 'transparent',
                        border: `1px solid ${isActive ? sourceStatus.accent : 'var(--border-subtle)'}`,
                      }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-[11px] font-medium" style={{ color: isActive ? sourceStatus.accent : 'var(--text-muted)' }}>
                          Section {index + 1}
                        </span>
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                          {countWords(section.body)} words
                        </span>
                      </div>
                      <p className="text-sm mt-1 leading-5" style={{ color: 'var(--text-primary)' }}>
                        {getSectionTitle(section)}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>
        )}

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 xl:px-10 py-6 sm:py-8">
            <div
              className="rounded-[28px] p-5 sm:p-6 mb-6"
              style={{
                background: 'var(--workspace-hero-bg)',
                border: '1px solid var(--workspace-surface-border)',
                boxShadow: 'var(--workspace-surface-shadow)',
              }}
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="max-w-3xl">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {templateSnapshot && (
                      <span
                        className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-medium"
                        style={{ background: sourceStatus.glow, color: sourceStatus.accent }}
                      >
                        <ShieldCheck size={12} />
                        {sourceStatus.label}
                      </span>
                    )}
                    <span
                      className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-medium"
                      style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}
                    >
                      {(doc.type || '').replace(/_/g, ' ')}
                    </span>
                    <span
                      className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-medium"
                      style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}
                    >
                      {wordCount} words
                    </span>
                    {workspace?.workspace_id && (
                      <span
                        className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-medium"
                        style={{ background: 'var(--workspace-chip-active-bg)', color: 'var(--accent-primary)' }}
                      >
                        {workspaceActiveSources.length} authorities in scope
                      </span>
                    )}
                  </div>
                  <h1 className="text-2xl sm:text-3xl leading-tight" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                    {templateSnapshot?.title || title}
                  </h1>
                  <p className="mt-3 text-sm sm:text-base leading-7" style={{ color: 'var(--text-secondary)' }}>
                    {templateSnapshot?.source_note || 'Draft this document in a structured workspace instead of a page-shaped text blob.'}
                  </p>
                  {(doc?.matter_context?.id || workspace?.matter_context?.id) && doc?.doc_id ? (
                    <div className="mt-4">
                      <CollaborationStrip
                        matterId={doc?.matter_context?.id || workspace?.matter_context?.id || ''}
                        targetType="document"
                        targetId={doc.doc_id}
                        targetLabel={title || templateSnapshot?.title || 'Draft'}
                        fallbackReviewState="new"
                        compact
                      />
                    </div>
                  ) : null}
                </div>

                {officialReferences.length > 0 && (
                  <div className="grid gap-2 lg:max-w-sm">
                    {officialReferences.slice(0, 2).map(ref => (
                      <a
                        key={ref.url}
                        href={ref.url}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-2xl px-4 py-3"
                        style={{ background: 'var(--workspace-surface-bg)', border: '1px solid var(--workspace-surface-border)' }}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{ref.title}</p>
                            <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>{ref.authority}</p>
                          </div>
                          <ExternalLink size={13} style={{ color: 'var(--text-muted)' }} />
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <section
              className="rounded-[24px] mb-6 overflow-hidden"
              style={{
                background: 'var(--workspace-surface-bg)',
                border: '1px solid var(--workspace-surface-border)',
                boxShadow: 'var(--workspace-surface-shadow-soft)',
              }}
              data-testid="drafting-support-panel"
            >
              <div className="px-5 sm:px-6 py-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span
                        className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-medium"
                        style={{ background: 'var(--workspace-chip-active-bg)', color: 'var(--accent-primary)' }}
                      >
                        <Scale size={12} />
                        Drafting mode
                      </span>
                      {trustSummary.matter_name && (
                        <span
                          className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-medium"
                          style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}
                        >
                          Matter: {trustSummary.matter_name}
                        </span>
                      )}
                    </div>
                    <h2 className="text-lg sm:text-xl leading-tight" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                      {draftingModeMeta.label || 'Draft workspace'}
                    </h2>
                    <p className="mt-2 text-sm leading-7" style={{ color: 'var(--text-secondary)' }}>
                      {draftingModeMeta.description || 'Keep the draft tight, source-backed, and court-usable.'}
                    </p>
                    {trustSummary.objective && (
                      <p className="mt-2 text-[12px] leading-6" style={{ color: 'var(--text-muted)' }}>
                        Current objective: {trustSummary.objective}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => fetchDraftingSupport()}
                      className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-medium"
                      style={{ background: 'var(--workspace-canvas-card-bg)', border: '1px solid var(--workspace-surface-border)', color: 'var(--text-secondary)' }}
                    >
                      <RefreshCw size={12} className={draftingSupportLoading ? 'animate-spin' : ''} />
                      {draftingSupportLoading ? 'Refreshing review' : 'Refresh review'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAssistant(open => !open)}
                      className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-medium"
                      style={{ background: showAssistant ? 'var(--workspace-chip-active-bg)' : 'var(--workspace-canvas-card-bg)', border: '1px solid var(--workspace-surface-border)', color: showAssistant ? 'var(--accent-primary)' : 'var(--text-secondary)' }}
                    >
                      <MessageSquare size={12} />
                      {showAssistant ? 'Hide counsel' : 'Open counsel'}
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {draftingModeOptions.map(option => {
                    const isActive = option.id === draftingMode;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => selectDraftingMode(option.id)}
                        className="rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors"
                        style={{
                          background: isActive ? 'var(--workspace-chip-active-bg)' : 'var(--workspace-chip-bg)',
                          color: isActive ? 'var(--accent-primary)' : 'var(--text-secondary)',
                          border: `1px solid ${isActive ? 'color-mix(in srgb, var(--accent-primary) 34%, var(--workspace-surface-border))' : 'var(--workspace-surface-border)'}`,
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>

                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 mt-5">
                  {[
                    { label: 'Source-backed', value: supportCounts['source-backed'] || 0, meta: SUPPORT_LABEL_META['source-backed'] },
                    { label: 'Matter-backed', value: supportCounts['matter-backed'] || 0, meta: SUPPORT_LABEL_META['matter-backed'] },
                    { label: 'Inferred', value: supportCounts.inferred || 0, meta: SUPPORT_LABEL_META.inferred },
                    { label: 'Review flags', value: draftingSupport?.validation?.finding_count || 0, meta: FINDING_SEVERITY_META.medium },
                  ].map(card => (
                    <div
                      key={card.label}
                      className="rounded-2xl px-4 py-3"
                      style={{ background: 'var(--workspace-canvas-card-bg)', border: '1px solid var(--workspace-surface-border)' }}
                    >
                      <p className="text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
                        {card.label}
                      </p>
                      <p className="mt-2 text-xl" style={{ color: card.meta.accent, fontFamily: 'var(--font-display)' }}>
                        {card.value}
                      </p>
                    </div>
                  ))}
                </div>

                {draftingSupportError && (
                  <p className="mt-4 text-sm" style={{ color: '#b45309' }}>
                    {draftingSupportError}
                  </p>
                )}

                <div className="grid gap-4 lg:grid-cols-[1.08fr,0.92fr] mt-5">
                  <div
                    className="rounded-2xl px-4 py-4"
                    style={{ background: 'var(--workspace-canvas-card-bg)', border: '1px solid var(--workspace-surface-border)' }}
                  >
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <p className="text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
                        Validation
                      </p>
                      {(draftingSupport?.validation?.high_severity_count || 0) > 0 && (
                        <span
                          className="rounded-full px-2.5 py-1 text-[10px] font-medium"
                          style={{ background: FINDING_SEVERITY_META.high.background, color: FINDING_SEVERITY_META.high.accent }}
                        >
                          {draftingSupport.validation.high_severity_count} high-risk
                        </span>
                      )}
                    </div>
                    {reviewFindings.length === 0 ? (
                      <p className="text-sm leading-7" style={{ color: 'var(--text-secondary)' }}>
                        No obvious support gaps were surfaced in the current pass. Refresh review after major edits or mode changes.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {reviewFindings.slice(0, 4).map(finding => {
                          const severityMeta = FINDING_SEVERITY_META[finding.severity] || FINDING_SEVERITY_META.low;
                          const supportMeta = SUPPORT_LABEL_META[finding.support_label] || SUPPORT_LABEL_META.unsupported;
                          return (
                            <button
                              key={finding.finding_id}
                              type="button"
                              onClick={() => {
                                if (!finding.section_id) return;
                                setActiveSectionId(finding.section_id);
                                sectionRefs.current[finding.section_id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }}
                              className="w-full rounded-2xl px-4 py-3 text-left"
                              style={{ background: 'var(--workspace-surface-bg)', border: '1px solid var(--workspace-surface-border)' }}
                            >
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className="rounded-full px-2.5 py-1 text-[10px] font-medium" style={{ background: severityMeta.background, color: severityMeta.accent }}>
                                  {severityMeta.label}
                                </span>
                                <span className="rounded-full px-2.5 py-1 text-[10px] font-medium" style={{ background: supportMeta.background, color: supportMeta.accent }}>
                                  {supportMeta.label}
                                </span>
                                {finding.section_title && (
                                  <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                                    {finding.section_title}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm leading-6" style={{ color: 'var(--text-primary)' }}>
                                {finding.title}
                              </p>
                              <p className="mt-1 text-[12px] leading-6" style={{ color: 'var(--text-secondary)' }}>
                                {finding.description}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div
                    className="rounded-2xl px-4 py-4"
                    style={{ background: 'var(--workspace-canvas-card-bg)', border: '1px solid var(--workspace-surface-border)' }}
                  >
                    <p className="text-[11px] uppercase tracking-[0.18em] mb-3" style={{ color: 'var(--text-muted)' }}>
                      Trusted Insertions
                    </p>
                    <div className="space-y-3">
                      {[...authorityInsertions.slice(0, 2), ...matterFactInsertions.slice(0, 2)].map(item => {
                        const supportMeta = SUPPORT_LABEL_META[item.support_label] || SUPPORT_LABEL_META.inferred;
                        return (
                          <div
                            key={item.id}
                            className="rounded-2xl px-4 py-3"
                            style={{ background: 'var(--workspace-surface-bg)', border: '1px solid var(--workspace-surface-border)' }}
                          >
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <span className="rounded-full px-2.5 py-1 text-[10px] font-medium" style={{ background: supportMeta.background, color: supportMeta.accent }}>
                                {supportMeta.label}
                              </span>
                              <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                                {item.trust_label || item.docsource || item.category}
                              </span>
                            </div>
                            <p className="text-sm leading-6" style={{ color: 'var(--text-primary)' }}>
                              {item.title}
                            </p>
                            <p className="mt-1 text-[12px] leading-6" style={{ color: 'var(--text-secondary)' }}>
                              {item.proposition_hint}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-3">
                              <button
                                type="button"
                                onClick={() => insertSupportNote(item.insert_text, item.support_label === 'source-backed' ? 'Authority note' : 'Matter note')}
                                className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-medium"
                                style={{ background: 'var(--workspace-chip-active-bg)', border: '1px solid color-mix(in srgb, var(--accent-primary) 26%, var(--workspace-surface-border))', color: 'var(--accent-primary)' }}
                              >
                                <Plus size={12} />
                                Insert
                              </button>
                              <button
                                type="button"
                                onClick={() => copySupportNote(item.insert_text, item.support_label === 'source-backed' ? 'authority note' : 'matter note')}
                                className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-medium"
                                style={{ background: 'var(--workspace-chip-bg)', border: '1px solid var(--workspace-surface-border)', color: 'var(--text-secondary)' }}
                              >
                                <Copy size={12} />
                                Copy
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {authorityInsertions.length === 0 && matterFactInsertions.length === 0 && (
                        <p className="text-sm leading-7" style={{ color: 'var(--text-secondary)' }}>
                          Trusted insertions will appear here once the draft is linked to stronger authority or matter support.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {workspace?.workspace_id && (
              <section
                className="rounded-[24px] mb-6 overflow-hidden"
                style={{
                  background: 'var(--workspace-surface-bg)',
                  border: '1px solid var(--workspace-surface-border)',
                  boxShadow: 'var(--workspace-surface-shadow-soft)',
                }}
              >
                <div className="px-5 sm:px-6 py-4">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span
                          className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-medium"
                          style={{ background: 'var(--workspace-chip-active-bg)', color: 'var(--accent-primary)' }}
                        >
                          <Scale size={12} />
                          Linked Research Posture
                        </span>
                        <span
                          className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-medium"
                          style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}
                        >
                          {workspaceTemplateLabel}
                        </span>
                        <span
                          className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-medium"
                          style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}
                        >
                          {workspaceActiveSources.length} authorities
                        </span>
                        <span
                          className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-medium"
                          style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}
                        >
                          {workspaceActiveUserDocIds.length} context docs
                        </span>
                      </div>

                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 flex-1">
                          <h2 className="text-lg sm:text-xl leading-tight" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                            {workspace?.title || 'Linked research workspace'}
                          </h2>
                          <p className="mt-2 text-sm leading-7" style={{ color: 'var(--text-secondary)' }}>
                            {workspacePostureSummary || 'This draft is linked to a research workspace so the writing stays grounded in the current brief, authorities, and selected context.'}
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => setShowWorkspaceContext(value => !value)}
                          className="inline-flex items-center justify-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-medium shrink-0"
                          style={{
                            border: '1px solid var(--workspace-surface-border)',
                            background: 'var(--workspace-canvas-card-bg)',
                            color: 'var(--text-secondary)',
                          }}
                        >
                          {showWorkspaceContext ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                          {showWorkspaceContext ? 'Hide detail' : 'Show detail'}
                        </button>
                      </div>

                      <div className="flex flex-wrap gap-2 mt-4">
                        {workspaceQuery && (
                          <span className="rounded-full px-3 py-1 text-[11px]" style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}>
                            Query: {compactText(workspaceQuery, 70)}
                          </span>
                        )}
                        <span className="rounded-full px-3 py-1 text-[11px]" style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}>
                          {workspaceScopeLabel}
                        </span>
                        <span className="rounded-full px-3 py-1 text-[11px]" style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}>
                          {workspaceFilterLabel}
                        </span>
                        {workspaceResearchState.research_type && (
                          <span className="rounded-full px-3 py-1 text-[11px]" style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}>
                            {formatResearchTypeLabel(workspaceResearchState.research_type)}
                          </span>
                        )}
                      </div>

                      {(compactAuthorityList.length > 0 || compactIssueList.length > 0) && (
                        <div className="grid gap-3 lg:grid-cols-[1.35fr,1fr] mt-4">
                          {compactAuthorityList.length > 0 && (
                            <div className="rounded-2xl px-4 py-3" style={{ background: 'var(--workspace-canvas-card-bg)', border: '1px solid var(--workspace-surface-border)' }}>
                              <p className="text-[11px] uppercase tracking-[0.16em] mb-2" style={{ color: 'var(--text-muted)' }}>
                                In Play Now
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {compactAuthorityList.map(source => (
                                  <span
                                    key={source.id || source.url || source.title}
                                    className="rounded-full px-2.5 py-1 text-[11px]"
                                    style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}
                                  >
                                    {compactText(source.title, 46)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {compactIssueList.length > 0 && (
                            <div className="rounded-2xl px-4 py-3" style={{ background: 'var(--workspace-canvas-card-bg)', border: '1px solid var(--workspace-surface-border)' }}>
                              <p className="text-[11px] uppercase tracking-[0.16em] mb-2" style={{ color: 'var(--text-muted)' }}>
                                Research Issues
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {compactIssueList.map(issue => (
                                  <span
                                    key={issue}
                                    className="rounded-full px-2.5 py-1 text-[11px]"
                                    style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}
                                  >
                                    {compactText(issue, 44)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4 pt-4" style={{ borderTop: '1px solid var(--workspace-surface-border)' }}>
                    <button
                      type="button"
                      onClick={openLinkedResearchWorkspace}
                      className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-medium"
                      style={{ background: 'var(--workspace-canvas-card-bg)', border: '1px solid var(--workspace-surface-border)', color: 'var(--text-secondary)' }}
                    >
                      <ExternalLink size={12} />
                      Open Research
                    </button>
                    <button
                      type="button"
                      onClick={refreshLinkedResearchWorkspace}
                      className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-medium"
                      style={{ background: 'var(--workspace-canvas-card-bg)', border: '1px solid var(--workspace-surface-border)', color: 'var(--text-secondary)' }}
                    >
                      <RefreshCw size={12} />
                      Refresh from Research
                    </button>
                    <button
                      type="button"
                      onClick={askAssistantWithCurrentContext}
                      className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-medium"
                      style={{ background: 'var(--workspace-chip-active-bg)', border: '1px solid color-mix(in srgb, var(--accent-primary) 26%, var(--workspace-surface-border))', color: 'var(--accent-primary)' }}
                    >
                      <MessageSquare size={12} />
                      Ask Research Counsel
                    </button>
                    <button
                      type="button"
                      onClick={copyPostureSummary}
                      className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-medium"
                      style={{ background: 'var(--workspace-canvas-card-bg)', border: '1px solid var(--workspace-surface-border)', color: 'var(--text-secondary)' }}
                    >
                      <Copy size={12} />
                      Copy Posture
                    </button>
                  </div>
                </div>

                {showWorkspaceContext && (
                  <div
                    className="px-5 sm:px-6 py-5 grid gap-4 lg:grid-cols-[1.15fr,0.95fr]"
                    style={{ borderTop: '1px solid var(--workspace-surface-border)', background: 'color-mix(in srgb, var(--workspace-canvas-card-bg) 86%, transparent)' }}
                  >
                    <div className="space-y-4">
                      {workspaceResearchBrief && (
                        <div className="rounded-2xl px-4 py-4" style={{ background: 'var(--workspace-canvas-card-bg)', border: '1px solid var(--workspace-surface-border)' }}>
                          <p className="text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: 'var(--text-muted)' }}>
                            Research Brief
                          </p>
                          <p className="text-sm leading-7 whitespace-pre-wrap" style={{ color: 'var(--text-secondary)' }}>
                            {compactText(workspaceResearchBrief, 720)}
                          </p>
                        </div>
                      )}

                      {compactPinnedList.length > 0 && (
                        <div className="rounded-2xl px-4 py-4" style={{ background: 'var(--workspace-canvas-card-bg)', border: '1px solid var(--workspace-surface-border)' }}>
                          <p className="text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: 'var(--text-muted)' }}>
                            Pinned Authorities
                          </p>
                          <div className="space-y-2">
                            {compactPinnedList.map(source => (
                              renderContextActionPopover({
                                kind: 'authority',
                                item: source,
                                itemKey: source.id || source.url || source.title,
                                trigger: (
                                  <button
                                    key={source.id || source.url || source.title}
                                    type="button"
                                    className="group flex w-full items-start gap-3 rounded-xl px-3 py-2 text-left transition-colors"
                                    style={{ background: 'color-mix(in srgb, var(--workspace-chip-bg) 58%, transparent)', border: 'none' }}
                                    title="Choose how Research Counsel should use this authority"
                                  >
                                    <span className="mt-1 h-1.5 w-1.5 rounded-full" style={{ background: 'var(--accent-primary)' }} />
                                    <span className="min-w-0 flex-1">
                                      <span className="block text-sm leading-6 transition-colors" style={{ color: 'var(--text-primary)' }}>
                                        {source.title}
                                      </span>
                                      {(source.docsource || source.publishdate) && (
                                        <span className="block text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                                          {[source.docsource, source.publishdate].filter(Boolean).join(' · ')}
                                        </span>
                                      )}
                                    </span>
                                    <span
                                      className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-medium shrink-0"
                                      style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-muted)' }}
                                    >
                                      Actions
                                      <ChevronDown size={11} />
                                    </span>
                                  </button>
                                ),
                              })
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div className="rounded-2xl px-4 py-4" style={{ background: 'var(--workspace-canvas-card-bg)', border: '1px solid var(--workspace-surface-border)' }}>
                        <p className="text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: 'var(--text-muted)' }}>
                          Drafting Posture
                        </p>
                        <div className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          <p><strong style={{ color: 'var(--text-primary)' }}>Workspace:</strong> {workspace?.title || 'Linked research workspace'}</p>
                          <p><strong style={{ color: 'var(--text-primary)' }}>Mode:</strong> {workspaceTemplateLabel}</p>
                          <p><strong style={{ color: 'var(--text-primary)' }}>Forum:</strong> {workspaceScopeLabel}</p>
                          <p><strong style={{ color: 'var(--text-primary)' }}>Source scope:</strong> {workspaceFilterLabel}</p>
                          <p><strong style={{ color: 'var(--text-primary)' }}>Context:</strong> {workspaceActiveSources.length} selected authorities, {workspaceActiveUserDocIds.length} selected documents</p>
                        </div>
                      </div>

                      {compactIssueList.length > 0 && (
                        <div className="rounded-2xl px-4 py-4" style={{ background: 'var(--workspace-canvas-card-bg)', border: '1px solid var(--workspace-surface-border)' }}>
                          <p className="text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: 'var(--text-muted)' }}>
                            Decomposed Issues
                          </p>
                          <div className="space-y-2">
                            {compactIssueList.map(issue => (
                              renderContextActionPopover({
                                kind: 'issue',
                                item: issue,
                                itemKey: issue,
                                trigger: (
                                  <button
                                    key={issue}
                                    type="button"
                                    className="group flex w-full items-start gap-3 rounded-xl px-3 py-2 text-left transition-colors"
                                    style={{ background: 'color-mix(in srgb, var(--workspace-chip-bg) 58%, transparent)', border: 'none' }}
                                    title="Choose how Research Counsel should work this issue"
                                  >
                                    <span className="mt-1 h-1.5 w-1.5 rounded-full" style={{ background: 'var(--accent-primary)' }} />
                                    <span className="min-w-0 flex-1 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>
                                      {issue}
                                    </span>
                                    <span
                                      className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-medium shrink-0"
                                      style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-muted)' }}
                                    >
                                      Actions
                                      <ChevronDown size={11} />
                                    </span>
                                  </button>
                                ),
                              })
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="rounded-2xl px-4 py-4" style={{ background: 'var(--workspace-canvas-card-bg)', border: '1px solid var(--workspace-surface-border)' }}>
                        <p className="text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: 'var(--text-muted)' }}>
                          Research Inputs
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <span className="rounded-full px-2.5 py-1 text-[11px]" style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}>
                            <Files size={11} style={{ display: 'inline', verticalAlign: 'text-bottom', marginRight: 6 }} />
                            {workspaceActiveUserDocIds.length} selected context docs
                          </span>
                          <span className="rounded-full px-2.5 py-1 text-[11px]" style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}>
                            {workspacePinnedSources.length} pinned sources
                          </span>
                          <span className="rounded-full px-2.5 py-1 text-[11px]" style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}>
                            {workspaceResearchSections.length} research sections
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            )}

            {isStructuredWorkspace && viewMode === 'structured' ? (
              <div className="space-y-5">
                {sections.map((section, index) => {
                  const isActive = section.id === activeSection?.id;
                  const headingLabel = section.document_heading ? `H${section.level || 2}` : 'BLOCK';
                  const isActivityRouter = section.id === 'activity_router';
                  return (
                    <section
                      key={section.id}
                      ref={node => { sectionRefs.current[section.id] = node; }}
                      className="rounded-[24px] overflow-hidden"
                      style={{
                        background: 'var(--workspace-canvas-card-bg)',
                        border: `1px solid ${isActive ? sourceStatus.accent : 'var(--workspace-surface-border)'}`,
                        boxShadow: isActive ? `0 0 0 1px ${sourceStatus.glow}` : 'var(--workspace-surface-shadow-soft)',
                      }}
                    >
                      <div
                        className="px-5 sm:px-6 py-4 flex flex-col gap-4 border-b"
                        style={{ borderColor: 'var(--border-subtle)', background: isActive ? sourceStatus.glow : 'var(--bg-elevated)' }}
                      >
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <span
                                className="inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold tracking-[0.18em]"
                                style={{ background: 'var(--workspace-chip-bg)', color: isActive ? sourceStatus.accent : 'var(--text-muted)' }}
                              >
                                {headingLabel}
                              </span>
                              <span className="text-[11px] font-medium uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>
                                {section.editor_label}
                              </span>
                            </div>
                            {section.document_heading ? (
                              section.locked_heading ? (
                                <h2 className="text-lg sm:text-xl leading-tight" style={{ fontFamily: 'var(--font-display)' }}>
                                  {section.document_heading}
                                </h2>
                              ) : (
                                <input
                                  value={section.document_heading}
                                  onFocus={() => setActiveSectionId(section.id)}
                                  onChange={event => updateStructuredSection(section.id, 'document_heading', event.target.value)}
                                  className="w-full bg-transparent border-none outline-none text-lg sm:text-xl"
                                  style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}
                                  placeholder="Section heading"
                                />
                              )
                            ) : (
                              <h2 className="text-lg sm:text-xl leading-tight" style={{ fontFamily: 'var(--font-display)' }}>
                                {section.editor_label}
                              </h2>
                            )}
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              onClick={() => moveSection(index, -1)}
                              className="p-2 rounded-xl"
                              style={{ background: 'var(--bg-surface)', color: 'var(--text-muted)', border: '1px solid var(--border-subtle)' }}
                              title="Move section up"
                            >
                              <ArrowUp size={14} />
                            </button>
                            <button
                              onClick={() => moveSection(index, 1)}
                              className="p-2 rounded-xl"
                              style={{ background: 'var(--bg-surface)', color: 'var(--text-muted)', border: '1px solid var(--border-subtle)' }}
                              title="Move section down"
                            >
                              <ArrowDown size={14} />
                            </button>
                          </div>
                        </div>

                        {(section.guidance || section.placeholders?.length) && (
                          <div className="grid gap-3 lg:grid-cols-[1.2fr,1fr]">
                            <div
                              className="rounded-2xl px-4 py-3"
                              style={{ background: 'var(--workspace-surface-bg)', border: '1px solid var(--workspace-surface-border)' }}
                            >
                              <p className="text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: 'var(--text-muted)' }}>
                                Drafting Guidance
                              </p>
                              <p className="text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>
                                {section.guidance || 'Keep the section concise, fact-led, and aligned with the source-backed structure.'}
                              </p>
                            </div>
                            <div
                              className="rounded-2xl px-4 py-3"
                              style={{ background: 'var(--workspace-surface-bg)', border: '1px solid var(--workspace-surface-border)' }}
                            >
                              <p className="text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: 'var(--text-muted)' }}>
                                Placeholders
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {(section.placeholders || []).length ? section.placeholders.map(token => (
                                  <span
                                    key={token}
                                    className="rounded-full px-2.5 py-1 text-[11px]"
                                    style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}
                                  >
                                    [{token}]
                                  </span>
                                )) : (
                                  <span className="text-sm" style={{ color: 'var(--text-muted)' }}>No fixed placeholders</span>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="px-5 sm:px-6 py-5">
                        {isActivityRouter ? (
                          <ActivityRouterSection section={section} />
                        ) : (
                          <textarea
                            value={section.body || ''}
                            onFocus={() => setActiveSectionId(section.id)}
                            onChange={event => updateStructuredSection(section.id, 'body', event.target.value)}
                            placeholder={`Draft ${getSectionTitle(section).toLowerCase()}...`}
                            className="w-full bg-transparent border-none outline-none resize-y"
                            style={{
                              minHeight: section.kind === 'signature' ? '140px' : '220px',
                              color: 'var(--text-primary)',
                              fontFamily: section.kind === 'caption' ? "'DM Sans', sans-serif" : "'IBM Plex Serif', 'Times New Roman', serif",
                              fontSize: section.kind === 'caption' ? '0.95rem' : '1rem',
                              lineHeight: '1.85',
                            }}
                          />
                        )}
                      </div>
                    </section>
                  );
                })}

                <button
                  onClick={addCustomSection}
                  className="w-full rounded-[20px] px-5 py-4 flex items-center justify-center gap-2"
                  style={{ border: '1px dashed var(--border-default)', color: 'var(--text-secondary)', background: 'transparent' }}
                >
                  <Plus size={14} />
                  Add Custom Section
                </button>
              </div>
            ) : (
              <div
                className="rounded-[28px] min-h-[70vh] p-5 sm:p-7"
                style={{
                  background: 'var(--workspace-hero-bg)',
                  border: '1px solid var(--workspace-surface-border)',
                  boxShadow: 'var(--workspace-surface-shadow)',
                }}
              >
                {isStructuredWorkspace ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <Columns size={15} />
                      Raw markdown preview of the structured template
                    </div>
                    <textarea
                      data-testid="doc-content-editor"
                      value={currentContent}
                      readOnly
                      className="w-full min-h-[55vh] bg-transparent border-none outline-none resize-none"
                      style={{
                        color: 'var(--text-primary)',
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: '0.82rem',
                        lineHeight: '1.8',
                      }}
                    />
                  </div>
                ) : (
                  <textarea
                    data-testid="doc-content-editor"
                    value={content}
                    onChange={event => setContent(event.target.value)}
                    className="w-full min-h-[60vh] bg-transparent border-none outline-none resize-none"
                    style={{
                      color: 'var(--text-primary)',
                      fontFamily: "'IBM Plex Serif', 'Times New Roman', serif",
                      fontSize: '1rem',
                      lineHeight: '1.9',
                    }}
                    placeholder="Start writing your legal document..."
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {showAssistant && (
          <div
            className="w-full max-w-[380px] shrink-0 flex flex-col"
            style={{ borderLeft: '1px solid var(--workspace-surface-border)', background: 'var(--workspace-rail-bg)' }}
            data-testid="ai-assistant-panel"
          >
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--workspace-surface-border)' }}>
              <div>
                <h3 className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'var(--font-display)' }}>
                  {assistantMode === 'research' ? <Bot size={14} /> : <MessageSquare size={14} />}
                  {assistantTitle}
                </h3>
                <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  {assistantSubtitle}
                </p>
              </div>
              <button onClick={() => setShowAssistant(false)} className="p-1 rounded hover:opacity-70">
                <X size={14} style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>

            {workspace?.workspace_id && (
              <div className="px-4 py-3 space-y-3" style={{ borderBottom: '1px solid var(--workspace-surface-border)' }}>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setAssistantMode('research')}
                    className="rounded-full px-3 py-1 text-[11px] font-medium"
                    style={{
                      background: assistantMode === 'research' ? 'var(--workspace-chip-active-bg)' : 'var(--workspace-chip-bg)',
                      color: assistantMode === 'research' ? 'var(--accent-primary)' : 'var(--text-secondary)',
                      border: `1px solid ${assistantMode === 'research' ? 'color-mix(in srgb, var(--accent-primary) 34%, var(--workspace-surface-border))' : 'var(--workspace-surface-border)'}`,
                    }}
                  >
                    Research Counsel
                  </button>
                  <button
                    type="button"
                    onClick={() => setAssistantMode('draft')}
                    className="rounded-full px-3 py-1 text-[11px] font-medium"
                    style={{
                      background: assistantMode === 'draft' ? 'var(--workspace-chip-active-bg)' : 'var(--workspace-chip-bg)',
                      color: assistantMode === 'draft' ? 'var(--accent-primary)' : 'var(--text-secondary)',
                      border: `1px solid ${assistantMode === 'draft' ? 'color-mix(in srgb, var(--accent-primary) 34%, var(--workspace-surface-border))' : 'var(--workspace-surface-border)'}`,
                    }}
                  >
                    Drafting Counsel
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full px-3 py-1 text-[11px]" style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}>
                    {workspaceActiveSources.length} selected authorities
                  </span>
                  <span className="rounded-full px-3 py-1 text-[11px]" style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}>
                    {workspaceActiveUserDocIds.length} context docs
                  </span>
                  {activeSection && (
                    <span className="rounded-full px-3 py-1 text-[11px]" style={{ background: 'var(--workspace-chip-bg)', color: 'var(--text-secondary)' }}>
                      Active: {getSectionTitle(activeSection)}
                    </span>
                  )}
                </div>
              </div>
            )}

            {aiMessages.length === 0 && (
              <div className="px-4 py-4 space-y-2">
                <div
                  className="rounded-2xl p-4"
                  style={{ background: 'var(--workspace-surface-bg)', border: '1px solid var(--workspace-surface-border)' }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={14} style={{ color: 'var(--accent-primary)' }} />
                    <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {activeSection ? `Active section: ${getSectionTitle(activeSection, 'No section selected')}` : 'Suggestions'}
                    </p>
                  </div>
                  <p className="text-xs leading-6" style={{ color: 'var(--text-secondary)' }}>
                    {workspace?.workspace_id
                      ? 'This assistant is attached to the originating research workspace, selected authorities, and the live master document.'
                      : isStructuredWorkspace
                        ? 'Ask for section-by-section rewriting, missing factual inputs, or a review of whether the draft still follows the official or statute-guided layout.'
                        : 'Ask for drafting help, missing clauses, or tone improvements.'}
                  </p>
                  {workspaceResearchBrief && (
                    <p className="text-[11px] mt-3 leading-6" style={{ color: 'var(--text-muted)' }}>
                      Research brief in scope: {workspaceResearchBrief.slice(0, 180)}{workspaceResearchBrief.length > 180 ? '…' : ''}
                    </p>
                  )}
                </div>
                {quickSuggestions.map((suggestion, index) => (
                  <button
                    key={`${suggestion}-${index}`}
                    onClick={() => setAiInput(suggestion)}
                    className="w-full text-left px-3 py-2.5 rounded-xl text-xs flex items-center gap-2"
                    style={{ color: 'var(--text-secondary)', background: 'var(--workspace-surface-bg)', border: '1px solid var(--workspace-surface-border)' }}
                  >
                    <ChevronRight size={12} />
                    {suggestion}
                  </button>
                ))}
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {aiMessages.map((message, index) => (
                <div key={index} className={message.role === 'user' ? 'text-right' : ''}>
                  <div
                    className="inline-block max-w-[92%] rounded-2xl px-3 py-2.5 text-left"
                    style={{
                      background: message.role === 'user' ? 'var(--workspace-surface-bg)' : 'var(--workspace-canvas-card-bg)',
                      border: `1px solid ${message.role === 'user' ? 'var(--workspace-surface-border)' : 'var(--border-subtle)'}`,
                    }}
                  >
                    {message.role === 'assistant' && (
                      <p className="text-[10px] uppercase tracking-[0.16em] mb-1" style={{ color: 'var(--text-muted)' }}>
                        {message.agent === 'draft' ? 'Drafting Counsel' : 'Research Counsel'}
                      </p>
                    )}
                    {message.role === 'assistant' ? (
                      message.content ? (
                        <StructuredAssistantMessage content={message.content} />
                      ) : message.isStreaming ? (
                        <span className="inline-flex gap-1">
                          <span className="typing-dot" />
                          <span className="typing-dot" />
                          <span className="typing-dot" />
                        </span>
                      ) : null
                    ) : (
                      <p className="text-xs leading-6" style={{ color: 'var(--text-primary)' }}>{message.content}</p>
                    )}
                  </div>
                  {message.role === 'assistant' && !message.isStreaming && message.content && (
                    <button
                      onClick={() => applyAiSuggestion(message.content)}
                      className="text-xs mt-1 px-2 py-0.5 rounded"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      + Apply to draft
                    </button>
                  )}
                </div>
              ))}
              <div ref={aiEndRef} />
            </div>

            <div className="px-3 pb-3 pt-2" style={{ borderTop: '1px solid var(--workspace-surface-border)' }}>
              <div className="relative rounded-2xl" style={{ background: 'var(--workspace-canvas-card-bg)', border: '1px solid var(--workspace-surface-border)' }}>
                <div style={{ padding: '10px 12px 0' }}>
                  <JollyDictationButton compact label="Jolly" disabled={aiStreaming} onAppendText={appendAiDictation} />
                </div>
                <textarea
                  value={aiInput}
                  onChange={event => setAiInput(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      sendAiMessage();
                    }
                  }}
                  className="w-full bg-transparent border-none outline-none resize-none px-3 py-3 pr-12 text-xs"
                  style={{ minHeight: '70px', maxHeight: '120px', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}
                  placeholder={workspace?.workspace_id
                    ? assistantMode === 'research'
                      ? 'Ask about authorities, gaps, structure, or the next section...'
                      : 'Ask for drafting help on the live document...'
                    : isStructuredWorkspace
                      ? 'Ask the template agent to improve the active section...'
                      : 'Ask AI to edit this document...'}
                  disabled={aiStreaming}
                  data-testid="ai-assistant-input"
                />
                <button
                  onClick={sendAiMessage}
                  disabled={!aiInput.trim() || aiStreaming}
                  data-testid="ai-assistant-send"
                  className="absolute right-2 bottom-2 p-2 rounded-xl"
                  style={{
                    background: aiInput.trim() ? 'var(--text-primary)' : 'transparent',
                    color: aiInput.trim() ? 'var(--bg-base)' : 'var(--text-muted)',
                  }}
                >
                  {aiStreaming ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      </main>
    </div>
  );
}
